import {
    Alert,
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { useCredenciaisApiOutletContext } from 'pages/CredenciaisApi'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiClientWithSecret, apiClients } from 'service/api'
import { toast } from 'sonner'

export default function RegenerarSegredoCredencial() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { data } = useCredenciaisApiOutletContext()
    const credencial = data?.find(item => item.id === Number(id))

    const [loading, setLoading] = useState(false)
    const [resultado, setResultado] = useState<ApiClientWithSecret | null>(null)
    const [copiedSecret, setCopiedSecret] = useState(false)

    if (!credencial) return null

    const handleClose = () => {
        setResultado(null)
        navigate('/integracoes/credenciais-api')
    }

    const handleRegenerar = async () => {
        setLoading(true)
        try {
            const res = (await apiClients.regenerarSegredo(
                credencial.id,
            )) as unknown as ApiClientWithSecret
            setResultado(res)
            await queryClient.invalidateQueries(apiClients.queryKey())
            toast.success('Segredo regenerado com sucesso')
        } catch (err: any) {
            toast.error(err?.message || 'Erro ao regenerar segredo')
        } finally {
            setLoading(false)
        }
    }

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedSecret(true)
            setTimeout(() => setCopiedSecret(false), 2000)
            toast.success('Copiado para a área de transferência')
        } catch {
            toast.error('Erro ao copiar')
        }
    }

    if (resultado) {
        return (
            <Dialog.Root height="auto" open onClose={handleClose}>
                <Dialog.Title>Novo Segredo Gerado</Dialog.Title>
                <Dialog.Content dividers>
                    <Alert severity="warning" className="mb-4">
                        <strong>Atenção:</strong> O segredo anterior foi
                        revogado e este novo segredo só será exibido esta vez!
                        Copie e guarde-o em um local seguro.
                    </Alert>

                    <Box className="flex flex-col gap-4">
                        <Box>
                            <Typography
                                variant="caption"
                                className="font-semibold text-muted-foreground"
                            >
                                Aplicação
                            </Typography>
                            <Typography variant="body1" className="font-medium">
                                {resultado.nome} ({resultado.clientId})
                            </Typography>
                        </Box>

                        <TextField
                            label="Novo Client Secret"
                            value={resultado.clientSecret}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() =>
                                                copyToClipboard(
                                                    resultado.clientSecret,
                                                )
                                            }
                                            edge="end"
                                            title="Copiar Novo Segredo"
                                        >
                                            {copiedSecret ? (
                                                <CheckIcon className="size-4 text-success" />
                                            ) : (
                                                <CopyIcon className="size-4" />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Dialog.Content>
                <Dialog.Actions>
                    <Button variant="contained" onClick={handleClose}>
                        Concluir
                    </Button>
                </Dialog.Actions>
            </Dialog.Root>
        )
    }

    return (
        <Dialog.Root
            width="auto"
            height="auto"
            loading={loading}
            open={hasPermission(['update', 'CredenciaisDeAPI'])}
            onClose={handleClose}
        >
            <Dialog.Title>Regenerar Segredo de API</Dialog.Title>
            <Dialog.Content>
                <Typography>
                    Tem certeza que deseja regenerar o segredo da credencial{' '}
                    <strong>{credencial.nome}</strong> ({credencial.clientId})?
                </Typography>
                <Alert severity="warning" className="mt-3">
                    As integrações que utilizam o segredo atual deixarão de
                    funcionar imediatamente.
                </Alert>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton onClick={handleClose} />
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleRegenerar}
                    disabled={loading}
                >
                    Regenerar Segredo
                </Button>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
